import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Bell, Loader2, Check, MessageSquare, Send, Copy } from "lucide-react";

// Reusable "Nudge worker" sheet: pings the app (push) to whoever installed it, then
// hands the owner a pre-filled SMS to text anyone else. Resolves the share link on open
// (via urlOverride when the caller already has it, otherwise from /reports/{id}/views).
export function NudgeSheet({ open, onOpenChange, reportId, jobName, location, dueDate, urlOverride }) {
  const [nudgeText, setNudgeText] = useState("");
  const [np, setNp] = useState(null);

  const dueSuffix = dueDate ? `\n\nPlease complete by ${format(new Date(`${String(dueDate).slice(0, 10)}T00:00:00`), "EEE, MMM d")}.` : "";
  const markSent = useCallback(() => { if (reportId) api.post(`/reports/${reportId}/mark-sent`).catch(() => {}); }, [reportId]);

  useEffect(() => {
    if (!open || !reportId) return;
    let cancelled = false;
    (async () => {
      setNp({ loading: true });
      let u = urlOverride || "";
      if (!u) {
        try {
          const { data } = await api.get(`/reports/${reportId}/views`);
          const token = data?.token;
          u = token ? `${window.location.origin}/api/s/${token}` : (data?.url || "");
        } catch { /* ignore */ }
      }
      if (cancelled) return;
      setNudgeText(`Quick reminder on the punch list for ${location || jobName || "your job"}.${dueSuffix}\n\nWhen you get a sec, snap a proof photo on each item and tap "Send proof back". Here's the link:\n${u}`);
      // Fire the app push to anyone who installed it; the result tells us who still needs a text.
      try { const { data } = await api.post(`/reports/${reportId}/nudge`); if (!cancelled) setNp(data); }
      catch { if (!cancelled) setNp({ error: true }); }
    })();
    return () => { cancelled = true; };
  }, [open, reportId]); // eslint-disable-line

  const sendSms = () => { markSent(); window.location.href = `sms:?&body=${encodeURIComponent(nudgeText)}`; };
  const share = async () => {
    markSent();
    if (navigator.share) { try { await navigator.share({ text: nudgeText }); return; } catch { /* cancelled */ } }
    window.location.href = `sms:?&body=${encodeURIComponent(nudgeText)}`;
  };
  const copy = () => { navigator.clipboard.writeText(nudgeText); toast.success("Reminder copied"); };

  let statusText, tone = "border-border bg-background text-muted-foreground", Icon = Bell, spin = false;
  if (!np || np.loading) { statusText = "Checking who already has the app…"; Icon = Loader2; spin = true; }
  else if (np.error) { statusText = "Couldn't ping the app — a text still works below."; }
  else if (!np.recipients) { statusText = "No one has the app installed yet — a text is the way to reach them."; }
  else if (np.sent > 0) { statusText = `App alert sent to ${np.sent} ${np.sent === 1 ? "worker" : "workers"} — text anyone else below.`; tone = "border-emerald-500/30 bg-emerald-500/10 text-emerald-600"; Icon = Check; }
  else if (np.skipped >= np.recipients) { statusText = "Already app-pinged in the last 6h — a text is a good backup."; tone = "border-amber-500/30 bg-amber-500/10 text-amber-600"; }
  else { statusText = "They have an account but notifications are off — send a text."; }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="nudge-sheet">
        <SheetHeader><SheetTitle>Nudge worker</SheetTitle></SheetHeader>
        <div data-testid="nudge-push-status" className={cn("mt-2 flex items-start gap-2 rounded-xl border px-3 py-2.5 text-[13px] leading-snug", tone)}>
          <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", spin && "animate-spin")} />
          <span>{statusText}</span>
        </div>
        <p className="mt-3 text-xs font-medium text-muted-foreground">Text anyone without the app — edit if you like, then send from your messages:</p>
        <textarea
          data-testid="nudge-text"
          value={nudgeText}
          onChange={(e) => setNudgeText(e.target.value)}
          rows={6}
          className="mt-2 w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm leading-snug focus:border-emerald-500 focus:outline-none"
        />
        <Button data-testid="nudge-send-sms" onClick={sendSms} className="mt-3 h-12 w-full gap-2 bg-amber-500 text-white hover:bg-amber-600">
          <MessageSquare className="h-4 w-4" /> Send via SMS
        </Button>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Button variant="outline" data-testid="nudge-share" onClick={share} className="h-11 gap-1"><Send className="h-4 w-4" /> Share via…</Button>
          <Button variant="outline" data-testid="nudge-copy" onClick={copy} className="h-11 gap-1"><Copy className="h-4 w-4" /> Copy</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
